<template>
  <div>
    <div style="margin-bottom: 20px;">
      <app-button
        :label="t('Adicionar Categoria de Produto')"
        icon="pi pi-plus"
        size="small"
        data-testid="so-product-categories-add-btn"
        @click="handleNew"
      />
    </div>

    <!-- SOProductCategories Table -->
    <s-o-product-categories-table
      ref="soProductCategoriesTableRef"
      @edit="handleEdit"
      @delete="handleDelete">
    </s-o-product-categories-table>

    <!-- SOProductCategoriesModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import SOProductCategoriesTable from './SOProductCategoriesTable.vue';
// SOProductCategoriesModal is centralized in ModalManager.vue
import { log } from '../../utils/DebugLogger';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface Props {
  translations?: Record<string, unknown>;
}

const props = withDefaults(defineProps<Props>(), {
  translations: () => ({})
});

// Get i18n composable
const { t } = useI18n();

// Reactive data
const soProductCategoriesTableRef = ref<DataTableInstance | null>(null);

// Methods
const loadData = async (): Promise<void> => {
  log('SOProductCategoriesPage', 'loadData', 'Reloading data');
  if (soProductCategoriesTableRef.value) {
    await soProductCategoriesTableRef.value.loadData();
  }
};

const handleNew = (): void => {
  log('SOProductCategoriesPage', 'handleNew', 'Opening modal for new product category via eventBus');
  eventBus.emit('modal.so-product-categories.open', { action: 'create' });
};

const handleEdit = (id?: Id): void => {
  log('SOProductCategoriesPage', 'handleEdit', 'Editing product category ID via eventBus', id);
  eventBus.emit('modal.so-product-categories.open', { id, action: 'edit' });
};

const handleDelete = (id?: Id, data: Record<string, unknown> | null = null): void => {
  log('SOProductCategoriesPage', 'handleDelete', 'Deleting product category ID via eventBus', id);
  eventBus.emit('modal.so-product-categories.open', { id, data, action: 'delete' });
};

// Lifecycle
onMounted(() => {
  log('SOProductCategoriesPage', 'mounted', 'Component mounted');

  // Set up eventBus listeners for backward compatibility
  eventBus.on('so-product-category.new', handleNew);
  eventBus.on('so-product-category.edit', handleEdit);
  eventBus.on('so-product-category.delete', handleDelete);
});

onBeforeUnmount(() => {
  log('SOProductCategoriesPage', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up eventBus listeners
  eventBus.off('so-product-category.new', handleNew);
  eventBus.off('so-product-category.edit', handleEdit);
  eventBus.off('so-product-category.delete', handleDelete);
});

// Expose methods for parent components
defineExpose({
  loadData,
  handleNew,
  handleEdit,
  handleDelete
});
</script>

<style scoped>
</style>
